import React from 'react';
import { string, shape, number, object, bool } from 'prop-types';
import polyline from '@mapbox/polyline';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { circlePolyline } from '../../util/maps';

const formatColor = color => color.replace(/^#/, '');

// Draw a 1-mile (~1609 meters) circle around the center
const fuzzyCircleOverlay = (center, mapsConfig) => {
  const strokeWeight = 1;
  const strokeColor = mapsConfig.fuzzy.circleColor || '#0088ff';
  const strokeOpacity = 0.5;
  const fillColor = mapsConfig.fuzzy.circleColor || '#0088ff';
  const fillOpacity = 0.2;

  const oneMileInMeters = 1609;
  const path = circlePolyline(center, oneMileInMeters);

  const styles = `-${strokeWeight}+${formatColor(strokeColor)}-${strokeOpacity}+${formatColor(
    fillColor
  )}-${fillOpacity}`;
  return `path${styles}(${encodeURIComponent(polyline.encode(path))})`;
};

const mapOverlay = (center, mapsConfig) => fuzzyCircleOverlay(center, mapsConfig);

// Drop a Google-style red pin directly on the exact location.
const markerOverlay = center => `pin-l+ea4335(${center.lng},${center.lat})`;

// Zoom used when showing the exact location with a pin (street/neighborhood level,
// matching how Google Maps opens a dropped pin).
const EXACT_LOCATION_ZOOM = 15;

// Mapbox Static Images API caps requested image dimensions at 1280px per side.
const MAPBOX_STATIC_IMAGE_MAX_SIDE = 1280;

const StaticMapboxMap = props => {
  const { address, center, zoom, mapsConfig, dimensions, showExactLocation } = props;
  const { width, height } = dimensions;
  const requestWidth = Math.min(width, MAPBOX_STATIC_IMAGE_MAX_SIDE);
  const requestHeight = Math.min(height, MAPBOX_STATIC_IMAGE_MAX_SIDE);

  const libLoaded = typeof window !== 'undefined' && window.mapboxgl;
  if (!libLoaded) return null;

  // When showing the exact location, render a pin marker (like Google Maps)
  // instead of the fuzzy privacy circle.
  const overlay = showExactLocation
    ? markerOverlay(center)
    : mapOverlay(center, mapsConfig);
  const effectiveZoom = showExactLocation ? EXACT_LOCATION_ZOOM : zoom;

  const src =
    'https://api.mapbox.com/styles/v1/mapbox/streets-v10/static' +
    (overlay ? `/${overlay}` : '') +
    `/${center.lng},${center.lat},${effectiveZoom}` +
    `/${requestWidth}x${requestHeight}` +
    `?access_token=${mapsConfig.mapboxAccessToken}`;

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%', userSelect: 'none' }}>
      {/* Static image, non-clickable */}
      <img
        src={src}
        alt={address}
        crossOrigin="anonymous"
        style={{
          position: 'absolute',
          // inset: 0,
          display: 'block',
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          pointerEvents: 'none', // ensures map is not clickable
        }}
      />
      {/* Address below map, normal text */}
      {/* {address && (
        <p
          style={{
            marginTop: '8px',
            fontWeight: 'normal', // not bold
            fontSize: '14px',
            color: '#333',
          }}
        >
          {address} (1 mile radius)
        </p>
      )} */}
    </div>
  );
};

StaticMapboxMap.defaultProps = {
  address: '',
  center: null,
  showExactLocation: false,
};

StaticMapboxMap.propTypes = {
  address: string,
  center: shape({
    lat: number.isRequired,
    lng: number.isRequired,
  }).isRequired,
  zoom: number.isRequired,
  mapsConfig: object.isRequired,
  dimensions: shape({
    width: number.isRequired,
    height: number.isRequired,
  }).isRequired,
  showExactLocation: bool,
};

export default lazyLoadWithDimensions(StaticMapboxMap, { maxWidth: '100%', maxHeight: '100%' });
